<script>
import { Group, Line, Rect, Text, RegularPolygon } from "svelte-konva";
import { markerState } from "@app/store/markerStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { viewportState } from "@app/lib/timeline/viewport.svelte.js";
import { themeState } from "@app/store/themeStore.svelte.js";
import { getTimelinePalette } from "@app/lib/timeline/timelinePalette.js";

/**
 * Marker lines for the Konva timeline.
 *
 * Reads from `markerState.lists[selected]` and draws one vertical line per
 * viewport-visible marker, colored from the list's `color`. Each marker has a
 * downward-pointing triangle head in the ruler band (same pattern as the
 * playhead). When `editorState.showMarkerComment` is true, the marker's comment
 * is rendered in a small label beside the head.
 *
 * @typedef {Object} Props
 * @property {number} y       - stage y where the vertical line starts (bottom of ruler band)
 * @property {number} height  - line length from y through tracks (playhead-style)
 * @property {boolean} [show] - external toggle (defaults to true)
 */

/** @type {Props} */
let { y = 0, height = 100, show = true } = $props();

const HEAD_RADIUS = 5;
const HEAD_Y_OFFSET = -2; // apex sits slightly into the ruler band, like the playhead

const LABEL_PADDING_X = 4;
const LABEL_PADDING_Y = 2;
const LABEL_FONT_SIZE = 10;
let palette = $derived(getTimelinePalette(themeState.effective));

let selectedList = $derived(markerState.lists?.[markerState.selected] || null);
let listColor = $derived(selectedList?.color || "#E74C3C");

let visibleMarkers = $derived.by(() => {
    if (!show || !selectedList || !viewportState.containerWidth) return [];
    const { scrollX, containerWidth, pxPerSec } = viewportState;
    const startSec = scrollX / pxPerSec;
    const endSec = (scrollX + containerWidth) / pxPerSec;
    const out = [];
    const markers = selectedList.markers || [];
    for (let i = 0; i < markers.length; i += 1) {
        const m = markers[i];
        const t = Number(m?.time);
        if (!Number.isFinite(t)) continue;
        if (t < startSec - 1 || t > endSec + 1) continue;
        out.push({
            id: m.id || `marker-${i}`,
            x: Math.round(t * pxPerSec - scrollX),
            comment: typeof m.comment === "string" ? m.comment : ""
        });
    }
    return out;
});

const showLabels = $derived(Boolean(editorState.showMarkerComment));
/** Place comment labels just below the triangle head in the ruler band. */
let labelY = $derived(y + HEAD_Y_OFFSET + HEAD_RADIUS + 2);
</script>

<!--
    Use stage-absolute coordinates (no Group y offset). Previously Group y={y}
    shifted the origin to the bottom of the ruler, so lines never drew inside
    the ruler band. Match TimelinePlayhead: triangle at y + HEAD_Y_OFFSET, line
    from y through y + height.
-->
<Group listening={false}>
    {#each visibleMarkers as marker (marker.id)}
        <Line
            points={[marker.x, y, marker.x, y + height]}
            stroke={listColor}
            strokeWidth={1.5}
            opacity={0.85}
            listening={false} />
        <RegularPolygon
            x={marker.x}
            y={y + HEAD_Y_OFFSET}
            sides={3}
            radius={HEAD_RADIUS}
            rotation={180}
            fill={listColor}
            opacity={0.95}
            listening={false} />
        {#if showLabels && marker.comment}
            {@const labelWidthEstimate = Math.min(180, marker.comment.length * 6 + LABEL_PADDING_X * 2)}
            <Rect
                x={marker.x + 2}
                y={labelY}
                width={labelWidthEstimate}
                height={LABEL_FONT_SIZE + LABEL_PADDING_Y * 2}
                fill={palette.markerLabelBg}
                cornerRadius={2}
                listening={false} />
            <Text
                x={marker.x + 2 + LABEL_PADDING_X}
                y={labelY + LABEL_PADDING_Y}
                text={marker.comment}
                width={labelWidthEstimate - LABEL_PADDING_X * 2}
                fontSize={LABEL_FONT_SIZE}
                fill={palette.markerLabelFg}
                ellipsis={true}
                wrap="none"
                listening={false} />
        {/if}
    {/each}
</Group>
